import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import FormPageLayout from "../../components/common/formkit/FormPageLayout";
import { Section, TextField, SelectField, TextAreaField } from "../../components/common/formkit/fields";
import { FocusFirstError, useUnsavedChangesGuard, allowLeaveOnce, useExitWizard } from "../../components/common/formkit/formHelpers";
import { showToast } from "../../components/common/notification/Notification";
import SaaSNotFoundState from "../../components/common/saas/SaaSNotFoundState";
import { getErrorText } from "./services/partnerNetworkService";
import { useListRecord, useNetworkService } from "./hooks/useNetworkRecords";
import { CLIENT_TYPES, isAlreadyAdded, toSimpleOptions } from "./utils/people";
import { CLIENTS_HOME } from "./constants/networkEndpoints";
import "./partnerNetwork.css";

const FORM_ID = "client-form";
const DEFAULT_COUNTRY = "India";
const NOTES_MAX = 2000;
const CLIENT_TYPE_OPTIONS = toSimpleOptions(CLIENT_TYPES);

// Same rules and messages as the mobile Add Contact form
const validationSchema = Yup.object({
  first_name: Yup.string().trim().required("First name is required"),
  last_name: Yup.string().trim().required("Last name is required"),
  mobile_number: Yup.string().required("Mobile number is required").matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits"),
  budget: Yup.string().max(100, "Budget must be at most 100 characters"),
  notes: Yup.string().max(NOTES_MAX, `Notes must be at most ${NOTES_MAX} characters`),
});

// The backend answers "<what> already available" for a duplicate; mobile words it as an email problem
const duplicateMessage = (text) =>
  text && text.includes("already available") ? `${text.split(" ")[2]} is already in use. Please use a different email.` : text;

const toOptions = (rows) => rows.map((r) => ({ value: r.name, label: r.name, id: r.id }));

/** Lives inside <Formik> so it can read the form state for the unsaved-changes warning. */
const ClientFormBody = ({ countries, states, cities, onCountrySelected, onStateSelected, saved, submitError }) => {
  const { dirty, values, setFieldValue } = useFormikContext();
  useUnsavedChangesGuard(dirty && !saved);

  const countryOptions = useMemo(() => toOptions(countries), [countries]);
  const stateOptions = useMemo(() => toOptions(states), [states]);
  const cityOptions = useMemo(() => toOptions(cities), [cities]);

  return (
    <Form id={FORM_ID} noValidate>
      <FocusFirstError />

      {submitError && (
        <div className="alert alert-danger pn-alert" role="alert">
          {submitError}
        </div>
      )}

      <Section title="Client details" hint="Add a client so you can keep their details in one place.">
        <div className="fk-grid">
          <TextField name="first_name" label="First name" required placeholder="Enter first name" autoComplete="off" />
          <TextField name="last_name" label="Last name" required placeholder="Enter last name" autoComplete="off" />
          <TextField name="mobile_number" label="Mobile number" required type="tel" inputMode="numeric" placeholder="Enter mobile number" autoComplete="off" />
          <TextField name="email_address" label="Email address" type="email" placeholder="Enter email address" autoComplete="off" />
        </div>
      </Section>

      <Section title="Location">
        <div className="fk-grid">
          <SelectField
            name="country_name"
            label="Country"
            options={countryOptions}
            placeholder="Select country"
            onSelect={(value, option) => {
              setFieldValue("state_name", "");
              setFieldValue("city_name", "");
              onCountrySelected(option.id);
            }}
          />
          <SelectField
            name="state_name"
            label="State"
            options={stateOptions}
            disabled={!values.country_name}
            placeholder="Select state"
            onSelect={(value, option) => {
              setFieldValue("city_name", "");
              onStateSelected(option.id);
            }}
          />
          <SelectField
            name="city_name"
            label="City"
            options={cityOptions}
            disabled={!values.state_name}
            helper={!values.state_name ? "Select a state first" : undefined}
            placeholder="Select city"
          />
          <TextField name="area_name" label="Area" placeholder="Enter area name" autoComplete="off" />
          <TextField name="zip_code" label="Zip code" inputMode="numeric" placeholder="Enter zip code" autoComplete="off" />
        </div>
      </Section>

      <Section title="Requirement" hint="Optional.">
        <div className="fk-grid">
          <SelectField name="client_type" label="Client type" options={CLIENT_TYPE_OPTIONS} placeholder="Select client type" />
          <TextField name="budget" label="Budget range" placeholder="e.g. 50L - 1Cr" autoComplete="off" maxLength={100} />
        </div>
        <TextAreaField name="notes" label="Notes" rows={4} maxLength={NOTES_MAX} placeholder="Anything worth remembering about this client" />
      </Section>
    </Form>
  );
};

/** /partner/network/clients/new and /partner/network/clients/:Id/edit */
const ClientFormPage = () => {
  const { Id } = useParams();
  const isEdit = Boolean(Id);
  const seed = useLocation().state?.record;
  const exit = useExitWizard(CLIENTS_HOME);
  const service = useNetworkService();

  // No get-by-id endpoint: the record to edit comes out of the client list. Adding needs no list at all.
  const loadRecords = useCallback(() => (Id ? service.listClients() : Promise.resolve([])), [Id, service]);
  const { record, loading: recordLoading, error, notFound, refetch } = useListRecord(loadRecords, Id, seed, "Unable to load this client.");

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Location lists are optional: if they fail to load the client can still be added without a location
  const loadStates = async (countryId) => {
    setStates(countryId ? await service.fetchStates(countryId).catch(() => []) : []);
    setCities([]);
  };

  const loadCities = async (stateId) => {
    setCities(stateId ? await service.fetchCities(stateId).catch(() => []) : []);
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const countryList = await service.fetchCountries().catch(() => []);
      const defaultCountry = countryList.find((c) => c.name === DEFAULT_COUNTRY);
      const stateList = defaultCountry ? await service.fetchStates(defaultCountry.id).catch(() => []) : [];
      if (cancelled) return;
      setCountries(countryList);
      setStates(stateList);
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [service]);

  // Editing: fill the city list for the saved state once the states are in (once per record)
  const citiesLoadedFor = useRef(null);
  useEffect(() => {
    if (!isEdit || !record?.state_name || states.length === 0 || citiesLoadedFor.current === record.id) return;
    citiesLoadedFor.current = record.id;
    const savedState = states.find((s) => s.name === record.state_name);
    if (savedState) loadCities(savedState.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, record, states]);

  const defaultCountry = countries.some((c) => c.name === DEFAULT_COUNTRY) ? DEFAULT_COUNTRY : "";
  const initialValues = {
    first_name: record?.first_name || "",
    last_name: record?.last_name || "",
    mobile_number: record?.mobile_number || "",
    email_address: record?.email_address || "",
    country_name: record?.country_name || defaultCountry,
    state_name: record?.state_name || "",
    city_name: record?.city_name || "",
    area_name: record?.area_name || "",
    zip_code: record?.zip_code || "",
    client_type: record?.client_type || "",
    budget: record?.budget || "",
    notes: record?.notes || "",
  };

  const handleSubmit = async (values, { setFieldError }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const payload = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        mobile_number: values.mobile_number.trim(),
        email_address: values.email_address.trim(),
        area_name: values.area_name.trim(),
        country_name: values.country_name,
        state_name: values.state_name,
        city_name: values.city_name,
        zip_code: values.zip_code.trim(),
        client_type: values.client_type,
        budget: values.budget.trim(),
        notes: values.notes.trim(),
      };
      if (isEdit) {
        await service.editClient(record, payload);
      } else {
        await service.addClient(payload);
        await service.rewardAddClient();
      }
      showToast(isEdit ? "Client updated successfully" : "Client added successfully", { type: "success" });
      setSaved(true); // lifts the unsaved-changes warning before leaving
      allowLeaveOnce();
      setTimeout(exit, 0);
    } catch (err) {
      // The form keeps its values so nothing is lost
      const text = duplicateMessage(getErrorText(err, isEdit ? "Unable to update client" : "Unable to add client"));
      if (isAlreadyAdded(text)) setFieldError("mobile_number", text);
      setSubmitError(text);
      showToast(text, { type: "error" });
      setSubmitting(false);
    }
  };

  if (isEdit && error) {
    return (
      <div className="fk-page" style={{ maxWidth: 860 }}>
        <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
          <span>{error}</span>
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={refetch}>
              Retry
            </button>
            <Link to={CLIENTS_HOME} className="btn btn-sm btn-outline-secondary">
              Back to Clients
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isEdit && notFound) {
    return <SaaSNotFoundState title="Client not found" description="This client could not be found. It may have been removed." backLink={CLIENTS_HOME} backLabel="Back to Clients" />;
  }

  return (
    <FormPageLayout
      title={isEdit ? "Edit Client" : "Add Client"}
      subtitle={isEdit ? "Update this client's details." : "Add a client to your network."}
      formId={FORM_ID}
      submitLabel={isEdit ? "Update Client" : "Add Client"}
      submitting={submitting}
      loading={loading || (isEdit && recordLoading)}
      onCancel={exit}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        <ClientFormBody
          countries={countries}
          states={states}
          cities={cities}
          onCountrySelected={loadStates}
          onStateSelected={loadCities}
          saved={saved}
          submitError={submitError}
        />
      </Formik>
    </FormPageLayout>
  );
};

export default ClientFormPage;
